import React, { useMemo, useCallback } from 'react';
import { Grid, Paper, Box, Divider } from '@mui/material';
import { Form } from 'react-final-form';
import { styled } from '@mui/material/styles';
import { useSnackbar } from 'notistack';
//
import Title from 'components/common/Title';
import { createFormFields } from './fields';
import {
  alertTypes,
  NEW,
  PAYMENT_ALERT,
  UBIDEX_GATEWAY,
} from '../../../../constants';
import { validate } from 'utils/validate';
import ButtonSubmit from 'components/common/ButtonSubmit';
import NotificationConfigDetails from 'components/form/whitelabel/components/NotificationConfigDetails';
import CopyDataButton from 'components/form/whitelabel/components/CopyDataButton';
import { handleApiError } from 'utils/common';
import {
  useGetWLIDByIDQuery,
  useUpdateWLIDGeneralByIDMutation,
} from '../../../../store/api/wl.api';

const FlexGridItem = styled(Grid)(() => ({
  display: 'flex',
  alignItems: 'flex-end',
}));

const initial = {
  paymentAlert: PAYMENT_ALERT,
  status: NEW,
  integrations: [UBIDEX_GATEWAY],
};

export default function General({ id, onFormStateChanged, editable }) {
  const { enqueueSnackbar } = useSnackbar();
  const decorators = useMemo(
    () => [
      ({ subscribe }) =>
        subscribe(({ dirty }) => onFormStateChanged(dirty), { dirty: true }),
    ],
    [onFormStateChanged],
  );
  const { data: generalInfo = initial, isSuccess: detailsFetched } =
    useGetWLIDByIDQuery(id, {
      skip: !id,
    });
  const [mutate] = useUpdateWLIDGeneralByIDMutation();

  const onSubmit = useCallback(
    async (values) => {
      try {
        const payload = {
          ...values,
          ...(id ? { id } : {}),
        };
        await mutate(payload).unwrap();
        enqueueSnackbar('Success!', { variant: 'success' });
      } catch (e) {
        handleApiError(e);
      }
    },
    [mutate, id, enqueueSnackbar],
  );

  const generalInitialValues = useMemo(
    () => ({
      ...generalInfo,
    }),
    [generalInfo],
  );

  if (!!id && !detailsFetched) {
    return <></>;
  }
  return (
    <Form
      onSubmit={onSubmit}
      initialValues={generalInitialValues}
      validate={validate}
      decorators={decorators}
      render={({ handleSubmit, submitting, values }) => (
        <form onSubmit={handleSubmit} noValidate>
          <Paper style={{ padding: 16 }}>
            <Grid container alignItems='flex-start' spacing={2}>
              {createFormFields(values, !!id, !editable).map((item, idx) => (
                <FlexGridItem size={{ xs: item.size }} key={idx}>
                  {item.field}
                  {item.isCopy ? (
                    <CopyDataButton value={values[item.name]} />
                  ) : null}
                </FlexGridItem>
              ))}
            </Grid>
            <Divider sx={{ my: '8px' }} />
            <Title>Notifications</Title>
            <NotificationConfigDetails
              disabled={!editable}
              flagValue={values.alerts?.[alertTypes.ssl_app]?.enabled}
              flagName={`alerts.${alertTypes.ssl_app}.enabled`}
              flagLabel='SSL Expiration Notification for App Domain'
              dateName={`alerts.${alertTypes.ssl_app}.endDate`}
              textName={`alerts.${alertTypes.ssl_app}.message`}
            />
            <NotificationConfigDetails
              disabled={!editable}
              flagValue={values.alerts?.[alertTypes.domain_app]?.enabled}
              flagName={`alerts.${alertTypes.domain_app}.enabled`}
              flagLabel='Domain Expiration Notification for App Domain'
              dateName={`alerts.${alertTypes.domain_app}.endDate`}
              textName={`alerts.${alertTypes.domain_app}.message`}
            />
            <NotificationConfigDetails
              disabled={!editable}
              flagValue={values.alerts?.[alertTypes.ssl_track]?.enabled}
              flagName={`alerts.${alertTypes.ssl_track}.enabled`}
              flagLabel='SSL Expiration Notification for Tracking Domain'
              dateName={`alerts.${alertTypes.ssl_track}.endDate`}
              textName={`alerts.${alertTypes.ssl_track}.message`}
            />
            <NotificationConfigDetails
              disabled={!editable}
              flagValue={values.alerts?.[alertTypes.domain_track]?.enabled}
              flagName={`alerts.${alertTypes.domain_track}.enabled`}
              flagLabel='Domain Expiration Notification for Tracking Domain'
              dateName={`alerts.${alertTypes.domain_track}.endDate`}
              textName={`alerts.${alertTypes.domain_track}.message`}
            />
            <Grid container alignItems='flex-start' spacing={2}>
              <Grid style={{ marginTop: 16 }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    position: 'relative',
                  }}
                >
                  <ButtonSubmit
                    name='save'
                    onSubmit={handleSubmit}
                    isPending={submitting}
                    disabled={!editable}
                    type='submit'
                  />
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </form>
      )}
    />
  );
}
